import React, { useContext, useEffect, useMemo, useState } from 'react';

import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@material-ui/core';
import CircularProgress from '@material-ui/core/CircularProgress';
import { Autocomplete } from '@material-ui/lab';
import { FiChevronDown } from 'react-icons/fi';
import { IoClose } from 'react-icons/io5';

import { ButtonTitleWithLoading } from '@vyce/core/src/components';
import {
  getFormFoldersRequest,
  getFormTemplateFoldersRequest,
} from '@vyce/core/src/api/forms';
import { FormFolderDoc, FormTemplateFolderDoc } from '@vyce/core/src/api/types';
import { AppTextField } from '@vyce/core/src/components/inputs/AppTextField';
import { appAutocompleteUseStyles } from '@vyce/core/src/components/controlled-inputs/ControlledAutocomplete';
import { NotificationContext } from '@vyce/core/src/contexts/notificationContext';

const FORM_FOLDERS_FETCH_LIMIT = 500;
const ROOT_OPTION_ID = '__root__';

type Option = { id: string; name: string };

interface Props {
  open: boolean;
  loading?: boolean;
  companyId: string;
  userId: string;
  folderId: string | null;
  currentParentId?: string | null;
  kind?: 'form' | 'template';
  onClose: () => void;
  onConfirm: (newParentId: string | null) => void | Promise<void>;
}

export const MoveFolderDialog: React.FC<Props> = ({
  open,
  loading = false,
  companyId,
  userId,
  folderId,
  currentParentId = null,
  kind = 'form',
  onClose,
  onConfirm,
}) => {
  const { handleServerError } = useContext(NotificationContext);
  const classes = appAutocompleteUseStyles({});
  const [allFolders, setAllFolders] = useState<FormFolderDoc[]>([]);
  const [loadingFolders, setLoadingFolders] = useState(false);
  const [selectedId, setSelectedId] = useState<string>(ROOT_OPTION_ID);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      try {
        setLoadingFolders(true);
        const { folders } =
          kind === 'template'
            ? await getFormTemplateFoldersRequest({
                companyId,
                userId,
                limit: FORM_FOLDERS_FETCH_LIMIT,
              })
            : await getFormFoldersRequest({
                companyId,
                userId,
                limit: FORM_FOLDERS_FETCH_LIMIT,
              });
        if (!cancelled) {
          setAllFolders(folders as unknown as FormFolderDoc[]);
          setSelectedId(currentParentId || ROOT_OPTION_ID);
        }
      } catch (error) {
        if (!cancelled) handleServerError(error);
      } finally {
        if (!cancelled) setLoadingFolders(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [companyId, userId, open, handleServerError, currentParentId, kind]);

  const disallowedIds = useMemo(() => {
    if (!folderId) return new Set<string>();
    const descendants = new Set<string>([folderId]);
    let changed = true;
    while (changed) {
      changed = false;
      allFolders.forEach(folder => {
        if (folder.parentId && descendants.has(folder.parentId) && !descendants.has(folder.id)) {
          descendants.add(folder.id);
          changed = true;
        }
      });
    }
    return descendants;
  }, [allFolders, folderId]);

  const rootOptionName = kind === 'template' ? 'Templates' : 'Forms';
  const options: Option[] = useMemo(
    () => [
      { id: ROOT_OPTION_ID, name: rootOptionName },
      ...allFolders
        .filter(folder => !disallowedIds.has(folder.id))
        .map(folder => ({ id: folder.id, name: folder.name })),
    ],
    [allFolders, disallowedIds, rootOptionName]
  );

  const selectedOption = options.find(option => option.id === selectedId) || options[0];
  const normalisedCurrentParent = currentParentId || ROOT_OPTION_ID;
  const canSave = selectedOption.id !== normalisedCurrentParent;

  const handleConfirm = () => {
    onConfirm(selectedOption.id === ROOT_OPTION_ID ? null : selectedOption.id);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Move folder</DialogTitle>
      <DialogContent>
        <Box display="flex" flexDirection="column" gridGap={8}>
          <Autocomplete
            options={options}
            classes={classes}
            value={selectedOption}
            onChange={(_, newValue) => setSelectedId(newValue?.id || ROOT_OPTION_ID)}
            popupIcon={<FiChevronDown size="25px" color="#A0A3BD" />}
            closeIcon={<IoClose size="25px" color="#A0A3BD" />}
            noOptionsText="Not Found"
            getOptionLabel={option => option?.name ?? ''}
            getOptionSelected={(option, value) => option.id === value.id}
            disableClearable
            fullWidth
            renderInput={params => (
              <AppTextField
                {...params}
                fullWidth
                label="Destination folder"
                margin="dense"
                autoFocus
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {loadingFolders ? (
                        <Box position="absolute" right={80} top={18}>
                          <CircularProgress color="secondary" size={20} />
                        </Box>
                      ) : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />
          {!loadingFolders && options.length === 1 && (
            <Typography variant="body2" color="textSecondary">
              No other folders available
            </Typography>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button size="large" variant="outlined" onClick={onClose} disabled={loading || loadingFolders}>
          Cancel
        </Button>
        <Button
          size="large"
          style={{ minWidth: 115 }}
          color="primary"
          onClick={handleConfirm}
          variant="contained"
          disabled={!canSave || loading || loadingFolders}
        >
          <ButtonTitleWithLoading title="Move" loaderVariant="paper" loading={!!loading} />
        </Button>
      </DialogActions>
    </Dialog>
  );
};
